import { Bot, Code2, Database, Layers, TestTube, Trophy, Rocket, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const modules = [
  {
    icon: Bot,
    tag: "Module 01",
    title: "Java + OOPS From Scratch",
    text: "Core Java, OOPS concepts, collections, exceptions — the exact foundation you need for Selenium. Taught the AI way: prompts, explainers, and hands-on code.",
    span: "lg:col-span-2",
  },
  {
    icon: TestTube,
    tag: "Module 02",
    title: "Selenium WebDriver",
    text: "Locators, waits, synchronization, dynamic elements, frames, windows, alerts — complete WebDriver mastery with real-time websites.",
  },
  {
    icon: Database,
    tag: "Module 03",
    title: "TestNG + Maven + GitHub",
    text: "Test annotations, assertions, parallel execution, data-driven tests, Maven builds, GitHub version control — everything an MNC expects.",
  },
  {
    icon: Layers,
    tag: "Module 04",
    title: "Hybrid Framework (POM + Page Factory)",
    text: "Design a real-time automation framework from scratch — Page Object Model, Page Factory, utilities, reports, config management. The industry-standard structure.",
    span: "lg:col-span-2",
  },
  {
    icon: Trophy,
    tag: "Module 05",
    title: "Cucumber BDD + Jenkins CI",
    text: "Gherkin, step definitions, hooks, tags — plus Jenkins setup for continuous integration. Go from manual tester to CI/CD-ready.",
  },
  {
    icon: Shield,
    tag: "Bonus",
    title: "Interview Prep + Live Project",
    text: "Real interview questions, mock rounds, a dummy project built end-to-end during the course. Walk in with answers, code, and confidence.",
  },
];

export default function Solution({ onCtaClick }) {
  return (
    <section
      id="curriculum"
      className="relative py-20 md:py-32 px-6 md:px-10 bg-[#0A1628]"
      data-testid="solution-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(228,255,0,0.06),transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-block text-xs font-normal tracking-[0.2em] uppercase text-[#38BDF8]">
              The BYMAT Method
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-normal text-white mt-4 leading-tight">
              Automation Testing, powered by{" "}
              <span className="text-[#38BDF8]">AI.</span>
              <br />
              <span className="text-slate-400">No coding fear. No fluff.</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-md leading-relaxed">
            A complete, mentor-led curriculum that takes you from Selenium basics to a
            production-ready framework — the fastest way to go from manual to
            automation engineer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="curriculum-grid">
          {modules.map((m, i) => (
            <div
              key={i}
              className={`card-hover relative bg-slate-900/60 border border-slate-800 rounded-2xl p-7 md:p-8 overflow-hidden ${m.span || ""}`}
              data-testid={`module-card-${i}`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center">
                  <m.icon className="w-6 h-6 text-[#38BDF8]" />
                </div>
                <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-normal">
                  {m.tag}
                </span>
              </div>
              <h3 className="font-display text-2xl font-normal text-white">
                {m.title}
              </h3>
              <p className="mt-3 text-slate-400 leading-relaxed">{m.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={onCtaClick}
            className="btn-yellow font-bold uppercase tracking-wider text-sm h-14 px-8 rounded-full"
            data-testid="solution-cta-button"
          >
            <Rocket className="w-4 h-4 mr-2" />
            Book My Seat @99/- Only
          </Button>
          <span className="text-slate-500 text-sm"> Get complete roadmap to transform your career to GenAI Automation Tester · Lifetime Recording Access</span>
        </div>
      </div>
    </section>
  );
}
